import React from "react";
import { Link } from "react-router-dom";
import { money, } from "../lib/api";
import { useStore } from "../context/StoreContext";
import FramedProductImage from "./FramedProductImage";

export function minPrice(product) {
  const prices = (product.variations || []).map((v) => v.promo_price || v.price);
  return prices.length ? Math.min(...prices) : 0;
}

export function hasPromo(product) {
  return (product.variations || []).some((v) => v.promo_price && v.promo_price < v.price);
}

export function resolveAdjust(product, settings) {
  if (product.image_adjust) return product.image_adjust;
  if (!settings) return null;
  const gx = settings.card_adjust_x || 0;
  const gy = settings.card_adjust_y || 0;
  const gs = settings.card_adjust_scale || 1;
  if (gx === 0 && gy === 0 && gs === 1) return null;
  return { fit: "contain", x: gx, y: gy, scale: gs };
}

export function bestPromo(product) {
  let best = null;
  for (const v of product.variations || []) {
    if (v.promo_price && v.promo_price < v.price) {
      const saving = v.price - v.promo_price;
      if (!best || saving > best.saving) best = { ...v, saving };
    }
  }
  return best;
}

export default function ProductCard({ product, auraColor }) {
  const { bootstrap } = useStore();
  const settings = bootstrap?.settings;
  // Menor preço oferecido entre as variações — o mesmo valor vai para a placa e para o texto abaixo
  const variations = product.variations || [];
  const price = variations.length ? Math.min(...variations.map((v) => v.promo_price || v.price)) : 0;
  const promoVar = variations.find((v) => (v.promo_price || v.price) === price && v.promo_price && v.promo_price < v.price) || null;
  const outOfStock = product.type === "physical" && (product.variations || []).every((v) => (v.stock || 0) <= 0);
  const fixedText = product.card_text || settings?.card_fixed_text || "";
  const pa = product.price_adjust || null;
  const priceDy = pa ? (pa.dy || 0) : (settings?.card_price_dy || 0);
  const priceScale = pa ? (pa.scale || 1) : (settings?.card_price_scale || 1);
  const priceColor = pa ? (pa.color || "#241600") : (settings?.card_price_color || "#241600");
  const priceWidth = pa ? (pa.width || 52) : (settings?.card_price_width || 52);
  const priceHeight = pa ? (pa.height || 6) : (settings?.card_price_height || 6);
  const priceDx = pa ? (pa.dx || 0) : (settings?.card_price_dx || 0);
  const priceRadius = pa ? (pa.radius ?? 2) : (settings?.card_price_radius ?? 2);

  return (
    <div className="relative">
      {auraColor && (
        <div
          aria-hidden
          className="absolute -bottom-16 left-1/2 -translate-x-1/2 w-3/4 h-14 rounded-full blur-2xl opacity-30 pointer-events-none transition-opacity duration-500 group-hover:opacity-55"
          style={{ background: auraColor }}
        />
      )}
      <Link
        to={`/produto/${product.slug}`}
        data-testid={`product-card-${product.slug}`}
        className="group relative z-10 block hover:-translate-y-1 transition-transform duration-300"
      >
        <div className="relative">
          <FramedProductImage
            image={product.images?.[0]}
            name={product.name}
            price={money(price)}
            adjust={resolveAdjust(product, settings)}
            priceDy={priceDy}
            priceDx={priceDx}
            priceScale={priceScale}
            priceColor={priceColor}
            priceWidth={priceWidth}
            priceHeight={priceHeight}
            priceRadius={priceRadius}
            testid="product-card-frame"
          />
          {product.type === "digital" && (
            <span className="absolute top-[6%] right-[8%] text-[10px] tracking-[0.15em] uppercase bg-black/80 border border-gold/40 text-gold px-2 py-1 rounded z-10">Digital</span>
          )}
          {promoVar && (
            <span className="absolute top-[6%] left-[8%] text-[10px] tracking-[0.15em] uppercase bg-imperial text-white px-2 py-1 rounded z-10">Oferta</span>
          )}
        </div>
        <div className="pt-3 px-1 text-center">
          {fixedText && (
            <p className="text-[10px] tracking-[0.2em] uppercase text-gold/80 mb-1" data-testid="card-fixed-text">{fixedText}</p>
          )}
          <h3 className="font-display text-lg text-zinc-100 group-hover:text-gold transition-colors leading-snug">{product.name}</h3>
          <div className="mt-1.5 text-sm" data-testid="card-price-line">
            {promoVar ? (
              <span className="text-zinc-400">
                de <span className="line-through">{money(promoVar.price)}</span> por <span className="text-gold font-semibold">{money(promoVar.promo_price)}</span>
              </span>
            ) : product.variations?.length > 1 ? (
              <span className="text-zinc-500 text-xs">a partir de <span className="text-gold">{money(price)}</span></span>
            ) : null}
          </div>
          {outOfStock && <p className="text-xs text-red-400 mt-1">Esgotado</p>}
        </div>
      </Link>
    </div>
  );
}
